import CreateUserModal from "./CreateUserModal";
import useGetAllUser from "./hooks/useGetAllUser";
import { useState, useMemo } from "react";
import PrimaryButton from "../../common-components/Buttons/PrimaryButton";
import { useTranslation } from 'react-i18next';
import ListLoader from "../../common-components/ListLoader";
import moment from "moment";
import AlertButton from "../../common-components/Buttons/AlertButton";
import useDeleteUser from "./hooks/useDeleteUser";
import { USER_TYPE } from "../../utils/userType";
import { ToastContainer } from "react-toastify";
import { FaPen } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import AddDoctor from "../add-doctor/AddDoctor";
import AddClinic from "../add-clinic/AddClinic";
import AddStaff from "../add-staff/AddStaff";

const UsersManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isCreateUserModal, setIsCreateUserModal] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const { allUsers, isUserLoading, getAllUsers } = useGetAllUser();
  const { onDelete, isUserDeleting } = useDeleteUser({ getAllUsers });
  const [currentItem, setCurrentItem] = useState({});
  const [isEditable, setIsEditable] = useState(false);
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [isClinicModalOpen, setIsClinicModalOpen] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const userDetails = JSON.parse(sessionStorage.getItem("userDetails"));
  const currentUserType = userDetails.userType || {};

  // Filter users by active status - combine all active users into single list
  const { allActiveUsers, inactiveUsers } = useMemo(() => {
    const activeUsers = allUsers.filter(user => !user.isDelete);
    const inactiveUsers = allUsers.filter(user => user.isDelete);

    return {
      allActiveUsers: activeUsers,
      inactiveUsers
    };
  }, [allUsers]);

  const onOpenModal = (value) => {
    setCurrentItem({});
    setIsCreateUserModal(value);
  };

  const onEdit = (item) => {
    const { userType } = item || {};
    if (userType === "DOCTOR") {
      setIsEditable(true);
      setCurrentItem(item);
      setIsDoctorModalOpen(true);
    } else if (userType === "ASSISTANT") {
      setIsEditable(true);
      setCurrentItem(item);
      setIsStaffModalOpen(true);
    } else {
      setIsEditable(true);
      setCurrentItem(item);
      setIsCreateUserModal(userType);
    }
  };

  // User list component to avoid repetition
  const UserList = ({ users, showActions = true }) => {
    if (isUserLoading) {
      return <ListLoader />;
    }

    if (users.length === 0) {
      return (
        <div className="p-2 flex justify-center text-red-500">
          No Users found !
        </div>
      );
    }

    return (
      <>
        {users.map((user) => {
          const {
            id,
            username,
            name,
            createdAt,
            mobileNumber,
            userType,
            isDelete,
          } = user || {};

          return (
            <div
              key={id}
              className={`grid ${showActions ? 'grid-cols-6' : 'grid-cols-5'} border-b items-center last:border-b-0 p-2`}
            >
              <div>{username}</div>
              <div>{name}</div>
              <div>{mobileNumber}</div>
              <div>{USER_TYPE[userType] || "--"}</div>
              <div>
                {moment(createdAt).format("DD-MM-yyyy | hh:mm A")}
              </div>
              {showActions && userType !== "HOSPITALADMIN" && (
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => onEdit(user)}>
                    <FaPen color="#4C6AF7" />
                  </button>
                  
                  {userType === "DOCTOR" && user.doctorId && (
                    <PrimaryButton
                      buttonName={t("Manage Slots")}
                      onClick={() => navigate(`/admin/slot-management/${user.doctorId}`)}
                    />
                  )}

                  <AlertButton
                    buttonName={
                      isUserDeleting && selectedId === id
                        ? "Deleting"
                        : t("Delete")
                    }
                    onClick={() => {
                      setSelectedId(id);
                      onDelete(id);
                    }}
                    disabled={isUserDeleting && selectedId === id}
                  />
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // Table header component
  const TableHeader = ({ showActions = true }) => (
    <div className={`grid px-2 py-2 text-base border-b uppercase items-center ${showActions ? 'grid-cols-6' : 'grid-cols-5'} bg-[#F9FAFB] text-[#6B7280]`}>
      <div>{t("Username")}</div>
      <div>{t("Name")}</div>
      <div>{t("Mobile Number")}</div>
      <div>{t("Role")}</div>
      <div>{t("Created At")}</div>
      {showActions && <div></div>}
    </div>
  );

  return (
    <div className="m-5 text-sm text-primary">
      <ToastContainer />
      <div className="my-5">
        <div className="flex items-center justify-between">
          <div className="text-base font-medium">{t("User Management")}</div>
          <div className="gap-5 flex items-center">
            {/* <PrimaryButton
              buttonName={t("Add Clinic")}
              onClick={() => setIsClinicModalOpen((prev) => !prev)}
            /> */}
            {/* {(currentUserType === "SUPERADMIN" || currentUserType === "HOSPITALADMIN") && (
              <PrimaryButton
                buttonName={t("Create User")}
                onClick={() => {
                  setIsEditable(false);
                  setCurrentItem({});
                  setIsCreateUserModal("MARKETING_USER"); // Default, user can change from dropdown
                }}
              />
            )} */}

            {/* <PrimaryButton
              buttonName={t("Add MMU Staff")}
              onClick={() => onOpenModal("MMUSTAFF")}
            />
            <PrimaryButton
              buttonName={t("Add MMU Head")}
              onClick={() => onOpenModal("MMUHEAD")}
            /> */}
          </div>
        </div>

        {/* All Users - Single List */}
        <div className="mt-5">
          <div className="text-base font-medium mb-2">{t("All Users")}</div>
          <div className="border">
            <TableHeader />
            <UserList users={allActiveUsers} />
          </div>
        </div>

        <hr className="my-5"></hr>

        {/* Inactive Users Section */}
        {/* <div className="text-base font-medium">{t("Inactive Users")}</div>
        <div className="mt-5 border">
          <TableHeader showActions={false} />
          <UserList users={inactiveUsers} showActions={false} />
        </div> */}
      </div>

      <CreateUserModal
        isCreateUserModal={isCreateUserModal}
        setIsCreateUserModal={setIsCreateUserModal}
        getAllUsers={getAllUsers}
        isEditable={isEditable}
        currentItem={currentItem}
        setCurrentItem={setCurrentItem}
        setIsEditable={setIsEditable}
      />
      <div>
        {isDoctorModalOpen && (
          <AddDoctor
            showModal={isDoctorModalOpen}
            setIsDoctorModalOpen={setIsDoctorModalOpen}
            isEditable={isEditable}
            currentItem={isEditable ? currentItem : {}}
            getAllUsers={getAllUsers}
          />
        )}
        {isStaffModalOpen && (
          <AddStaff
            showModal={isStaffModalOpen}
            setIsStaffModalOpen={setIsStaffModalOpen}
            isEditable={isEditable}
            currentItem={isEditable ? currentItem : {}}
            getAllUsers={getAllUsers}
          />
        )}
      </div>
    </div>
  );
};

export default UsersManagement;
